import { LOCAL_STORAGE_PROJECT } from "../consts";

/**
 * Get project ID from URL query parameters
 * @returns {string|null} Project ID from URL params or null if not found
 */
export const getProjectIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("project");
};

/**
 * Get robot ID from URL query parameters
 * @returns {string|null} Robot ID from URL params or null if not found
 */
export const getRobotIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("robot");
};

/**
 * Set project ID in URL query parameters
 * @param {string} projectId - The project ID to set in URL
 * @param {boolean} replace - Whether to replace current history entry (default: false)
 */
export const setProjectIdInUrl = (projectId, replace = false) => {
  const url = new URL(window.location);
  if (projectId) {
    url.searchParams.set("project", projectId);
  } else {
    url.searchParams.delete("project");
  }

  if (replace) {
    window.history.replaceState({}, "", url);
  } else {
    window.history.pushState({}, "", url);
  }
};

/**
 * Set robot ID in URL query parameters
 * @param {string} robotId - The robot ID to set in URL
 * @param {boolean} replace - Whether to replace current history entry (default: false)
 */
export const setRobotIdInUrl = (robotId, replace = false) => {
  const url = new URL(window.location);
  if (robotId) {
    url.searchParams.set("robot", robotId);
  } else {
    url.searchParams.delete("robot");
  }

  if (replace) {
    window.history.replaceState({}, "", url);
  } else {
    window.history.pushState({}, "", url);
  }
};

/**
 * Navigate to a project-specific route with project ID in query params
 * @param {string} path - The path to navigate to
 * @param {string} projectId - The project ID to include in URL
 * @param {boolean} useWindowLocation - Whether to use window.location.href (default: true)
 */
export const navigateToProjectRoute = (
  path,
  projectId,
  useWindowLocation = true
) => {
  const url = new URL(path, window.location.origin);
  if (projectId) {
    url.searchParams.set("project", projectId);
  }

  if (useWindowLocation) {
    window.location.href = url.toString();
  } else {
    window.history.pushState({}, "", url);
  }
};

/**
 * Navigate to a robot-specific route with both project and robot ID in query params
 * @param {string} path - The path to navigate to
 * @param {string} projectId - The project ID to include in URL
 * @param {string} robotId - The robot ID to include in URL
 * @param {boolean} useWindowLocation - Whether to use window.location.href (default: true)
 */
export const navigateToRobotRoute = (
  path,
  projectId,
  robotId,
  useWindowLocation = true
) => {
  const url = new URL(path, window.location.origin);
  if (projectId) {
    url.searchParams.set("project", projectId);
  }
  if (robotId) {
    url.searchParams.set("robot", robotId);
  }

  if (useWindowLocation) {
    window.location.href = url.toString();
  } else {
    window.history.pushState({}, "", url);
  }
};

/**
 * Get current project from localStorage using URL parameter
 * @returns {Object|null} Current project object or null
 */
export const getCurrentProject = () => {
  const projectIdFromUrl = getProjectIdFromUrl();

  if (!projectIdFromUrl) {
    return null;
  }

  // Validate project ID format (basic validation)
  if (typeof projectIdFromUrl !== "string" || projectIdFromUrl.trim() === "") {
    console.warn("Invalid project ID format:", projectIdFromUrl);
    return null;
  }

  // Get projects from new multi-project storage
  const allProjects = getAllProjectsFromStorage();
  const project = allProjects[projectIdFromUrl];

  if (project) {
    // Validate project object structure
    if (!project._id || typeof project._id !== "string") {
      console.warn("Invalid project structure found in storage:", project);
      return null;
    }
    return project;
  }

  // Fallback: try to get from old single project storage for backward compatibility
  const storedProject = localStorage.getItem(LOCAL_STORAGE_PROJECT);
  if (storedProject && storedProject !== "null") {
    try {
      const parsedProject = JSON.parse(storedProject);
      if (parsedProject._id === projectIdFromUrl) {
        // Validate project structure before migrating
        if (!parsedProject._id || typeof parsedProject._id !== "string") {
          console.warn(
            "Invalid project structure in old storage:",
            parsedProject
          );
          return null;
        }
        // Migrate to new storage format
        setCurrentProject(parsedProject, false);
        return parsedProject;
      }
    } catch (e) {
      console.error("Error parsing stored project:", e);
    }
  }

  return null;
};

/**
 * Store project in localStorage and update URL
 * @param {Object} project - Project object to store
 * @param {boolean} updateUrl - Whether to update URL with project ID (default: true)
 */
export const setCurrentProject = (project, updateUrl = true) => {
  if (!project) {
    if (updateUrl) {
      setProjectIdInUrl(null, true);
    }
    return;
  }

  // Store in multi-project storage
  const allProjects = getAllProjectsFromStorage();
  allProjects[project._id] = project;
  localStorage.setItem("all_projects", JSON.stringify(allProjects));

  // Also update old storage for backward compatibility (store current project for components that still use it)
  localStorage.setItem(LOCAL_STORAGE_PROJECT, JSON.stringify(project));

  // Update URL if requested
  if (updateUrl) {
    setProjectIdInUrl(project._id, true);
  }
};

/**
 * Get all projects from localStorage (for multi-project support)
 * @returns {Object} Object with projectId as key and project object as value
 */
export const getAllProjectsFromStorage = () => {
  try {
    const allProjects = localStorage.getItem("all_projects");
    if (allProjects) {
      return JSON.parse(allProjects);
    }

    // Fallback: try old projects_history for backward compatibility
    const projectsHistory = localStorage.getItem("projects_history");
    if (projectsHistory) {
      const projects = JSON.parse(projectsHistory);
      // Migrate to new storage
      localStorage.setItem("all_projects", JSON.stringify(projects));
      return projects;
    }

    return {};
  } catch (e) {
    console.error("Error parsing projects from storage:", e);
    return {};
  }
};

/**
 * Get projects history from localStorage (for backward compatibility)
 * @returns {Object} Object with projectId as key and project object as value
 * @deprecated Use getAllProjectsFromStorage instead
 */
export const getProjectsFromStorage = () => {
  return getAllProjectsFromStorage();
};

/**
 * Clear all projects from storage
 */
export const clearAllProjects = () => {
  localStorage.removeItem(LOCAL_STORAGE_PROJECT);
  localStorage.removeItem("all_projects");
  localStorage.removeItem("projects_history"); // Remove old storage too
};

/**
 * Get current project for a specific tab/URL (used by components that need current project)
 * This is the main function components should use to get project data
 * @returns {Object|null} Current project object or null
 */
export const getCurrentProjectForTab = () => {
  return getCurrentProject();
};

/**
 * Check if current route requires a project to be selected
 * @returns {boolean} True if current route requires project
 */
export const isProjectRequiredRoute = () => {
  const projectRequiredRoutes = [
    "/dashboard",
    "/robots",
    "/robot",
    "/vehicle",
    "/vehicles/dashboard",
    "/vehicles/list",
    "/robot-site-info",
    "/robot-settings",
    "/tasks",
    "/messages",
    "/errors",
    "/error-resolution",
    "/daily-activity",
    "/availability",
    "/reports",
    "/weather",
    "/settings",
    "/settings/advanced",
    "/repeaters",
  ];

  const currentPath = window.location.pathname;
  return projectRequiredRoutes.some(
    (route) =>
      currentPath === route ||
      currentPath.startsWith(route + "/") ||
      (route.includes("[") &&
        currentPath.match(new RegExp(route.replace(/\[.*?\]/g, "[^/]+"))))
  );
};

/**
 * Redirect to projects page if no project is selected and current route requires one
 */
export const redirectIfNoProject = (navigate) => {
  if (isProjectRequiredRoute()) {
    const currentProject = getCurrentProject();
    if (!currentProject) {
      navigate("/projects");
      return true;
    }
  }
  return false;
};

/**
 * Generate full URL for project dashboard
 * @param {Object} project - Project object
 * @returns {string} Full URL to project dashboard
 */
export const getProjectDashboardUrl = (project) => {
  const targetRoute =
    project.arrayType === "fixed" ? "/dashboard" : "/vehicles/dashboard";
  const url = new URL(targetRoute, window.location.origin);
  if (project._id) {
    url.searchParams.set("project", project._id);
  }
  return url.toString();
};
